import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { motion } from "framer-motion";

import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Button,
    CircularProgress,
    MenuItem,
} from "@mui/material";

import {
    ArrowBack,
    School,
    Person,
    Email,
    Phone,
    LocationOn,
    MenuBook,
    Send,
    VerifiedUser,
    CheckCircle,
} from "@mui/icons-material";

import api from "../api/api";


/* =========================================================
   BRAND COLORS
========================================================= */

const COLORS = {
    black: "#07100A",

    greenDark: "#075B2A",
    green: "#16803A",
    greenBright: "#20A84A",

    lemon: "#C8F23A",
    lemonBright: "#B8F000",

    yellow: "#F5D32F",
    yellowBright: "#FFE45A",

    white: "#FFFFFF",
    text: "#172019",
    muted: "#6D776F",

    border: "#DCE6DE",
    paleGreen: "#EFF9F1",
    paleYellow: "#FFFBE8",
    input: "#FCFEFC",
};


/* =========================================================
   CUSTOM SWEETALERT
========================================================= */

const showAlert = ({
    icon,
    title,
    text,
    confirmButtonText = "Continue",
}) => {
    return Swal.fire({
        icon,
        title,
        text,

        confirmButtonText,

        background: COLORS.white,
        color: COLORS.text,

        confirmButtonColor: COLORS.green,

        buttonsStyling: true,

        customClass: {
            popup: "passion-application-alert",
            title: "passion-application-alert-title",
            htmlContainer: "passion-application-alert-text",
            confirmButton: "passion-application-alert-button",
        },

        showClass: {
            popup: "swal2-show passion-alert-show",
        },

        hideClass: {
            popup: "swal2-hide passion-alert-hide",
        },
    });
};


/* =========================================================
   MAIN COMPONENT
========================================================= */

const ApplyForEntranceForm = ({ onClose }) => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        program: "",
        education_level: "",
        location: "",
    });

    const [loading, setLoading] = useState(false);


    /* =====================================================
       PROGRAMS
    ===================================================== */

    const programs = useMemo(
        () => [
            "Professional Education",
            "Vocational Skills",
            "Leadership Development",
            "Entrepreneurship",
            "Compliance & Justice",
            "Community Empowerment",
        ],
        []
    );


    /* =====================================================
       EDUCATION LEVELS
    ===================================================== */

    const educationLevels = useMemo(
        () => [
            "Secondary School",
            "High School Graduate",
            "Certificate",
            "Diploma",
            "Undergraduate",
            "Graduate",
            "Professional",
            "Other",
        ],
        []
    );


    /* =====================================================
       HANDLE CHANGE
    ===================================================== */

    const handleChange = (field) => (event) => {
        setFormData((previous) => ({
            ...previous,
            [field]: event.target.value,
        }));
    };


    /* =====================================================
       VALIDATION
    ===================================================== */

    const validateForm = () => {
        const requiredFields = [
            "first_name",
            "last_name",
            "email",
            "phone",
            "program",
        ];

        const missingField = requiredFields.some(
            (field) => !formData[field].trim()
        );

        if (missingField) {
            showAlert({
                icon: "warning",
                title: "Incomplete Application",
                text:
                    "Please complete all required fields before submitting your application.",
                confirmButtonText: "Review Application",
            });

            return false;
        }

        const emailPattern =
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailPattern.test(formData.email.trim())) {
            showAlert({
                icon: "warning",
                title: "Check Your Email",
                text:
                    "Please enter a valid email address.",
                confirmButtonText: "Correct Email",
            });

            return false;
        }

        if (formData.phone.trim().length < 7) {
            showAlert({
                icon: "warning",
                title: "Check Your Phone Number",
                text:
                    "Please provide a valid phone number.",
                confirmButtonText: "Correct Number",
            });

            return false;
        }

        return true;
    };




// const handleSubmit = async (event) => {
//     event.preventDefault();

//     if (!validateForm()) {
//         return;
//     }

//     setLoading(true);

//     try {
//         const payload = {
//             first_name: formData.first_name.trim(),
//             last_name: formData.last_name.trim(),
//             email: formData.email.trim().toLowerCase(),
//             phone: formData.phone.trim(),
//             program: formData.program.trim(),
//             education_level: formData.education_level.trim(),
//             location: formData.location.trim(),
//         };

//         const response = await api.post(
//             "/api/entrance-applications/",
//             payload
//         );

//         console.log(
//             "ENTRANCE APPLICATION RESPONSE:",
//             response.data
//         );

//         const application =
//             response?.data?.application;

//         const applicationNumber =
//             application?.application_number;

//         await Swal.fire({
//             icon: "success",
//             title: "Application Received!",
//             html: `
//                 <div style="
//                     text-align:center;
//                     padding:4px 8px;
//                 ">
//                     <div style="
//                         width:64px;
//                         height:64px;
//                         margin:0 auto 16px;
//                         border-radius:50%;
//                         display:flex;
//                         align-items:center;
//                         justify-content:center;
//                         background:#EFF9F1;
//                         border:1px solid #D4EED9;
//                     ">
//                         <span style="
//                             font-size:30px;
//                             color:#16803A;
//                         ">
//                             ✓
//                         </span>
//                     </div>

//                     <p style="
//                         margin:0 0 10px;
//                         color:#68736C;
//                         font-size:14px;
//                         line-height:1.7;
//                     ">
//                         Thank you for your interest in
//                         <strong style="color:#16803A;">
//                             PASSION AIDS THE FUTURE
//                         </strong>.
//                     </p>

//                     ${
//                         applicationNumber
//                             ? `
//                                 <div style="
//                                     margin:16px auto;
//                                     padding:12px 16px;
//                                     max-width:290px;
//                                     border-radius:10px;
//                                     background:#FFFBE8;
//                                     border:1px solid #F5D32F;
//                                 ">
//                                     <div style="
//                                         color:#7A6810;
//                                         font-size:10px;
//                                         font-weight:800;
//                                         letter-spacing:1px;
//                                         margin-bottom:5px;
//                                     ">
//                                         APPLICATION NUMBER
//                                     </div>

//                                     <div style="
//                                         color:#16803A;
//                                         font-size:17px;
//                                         font-weight:900;
//                                         letter-spacing:.5px;
//                                     ">
//                                         ${applicationNumber}
//                                     </div>
//                                 </div>
//                             `
//                             : ""
//                     }

//                     <p style="
//                         margin:12px 0 0;
//                         color:#68736C;
//                         font-size:13px;
//                         line-height:1.7;
//                     ">
//                         Your entrance application has been
//                         submitted successfully. Continue to the
//                         next step to enter your application number
//                         and proceed with the payment procedure.
//                     </p>
//                 </div>
//             `,
//             confirmButtonText:
//                 "Continue to Enter Payment Procedure",
//             confirmButtonColor: COLORS.green,
//             background: COLORS.white,
//             color: COLORS.text,
//             allowOutsideClick: false,
//             allowEscapeKey: false,
//             customClass: {
//                 popup: "passion-application-alert",
//                 title: "passion-application-alert-title",
//                 confirmButton:
//                     "passion-application-alert-button",
//             },
//             showClass: {
//                 popup: "swal2-show passion-alert-show",
//             },
//             hideClass: {
//                 popup: "swal2-hide passion-alert-hide",
//             },
//         }).then(() => {
//             navigate("/entranceLogin");
//         });

//         setFormData({
//             first_name: "",
//             last_name: "",
//             email: "",
//             phone: "",
//             program: "",
//             education_level: "",
//             location: "",
//         });
//     } catch (error) {
//         console.error(
//             "ENTRANCE APPLICATION ERROR:",
//             error
//         );

//         const responseData =
//             error?.response?.data;

//         let message =
//             "We could not submit your application at this time. Please try again.";

//         if (responseData?.detail) {
//             message = String(responseData.detail);
//         } else if (responseData?.message) {
//             message = String(responseData.message);
//         } else if (
//             responseData &&
//             typeof responseData === "object"
//         ) {
//             const messages = [];

//             Object.entries(responseData).forEach(
//                 ([field, value]) => {
//                     if (Array.isArray(value)) {
//                         value.forEach((item) => {
//                             if (
//                                 item !== null &&
//                                 item !== undefined
//                             ) {
//                                 messages.push(
//                                     String(item)
//                                 );
//                             }
//                         });
//                     } else if (
//                         typeof value === "string"
//                     ) {
//                         messages.push(value);
//                     }
//                 }
//             );

//             if (messages.length > 0) {
//                 message = messages.join("\n");
//             }
//         }

//         showAlert({
//             icon: "error",
//             title: "Application Not Submitted",
//             text: message,
//             confirmButtonText: "Try Again",
//         });
//     } finally {
//         setLoading(false);
//     }
// };


const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
        return;
    }

    setLoading(true);

    try {
        const payload = {
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            email: formData.email.trim().toLowerCase(),
            phone: formData.phone.trim(),
            program: formData.program.trim(),
            education_level:
                formData.education_level.trim(),
            location: formData.location.trim(),
        };

        const response = await api.post(
            "/api/entrance-applications/",
            payload
        );

        console.log(
            "ENTRANCE APPLICATION RESPONSE:",
            response.data
        );

        const application =
            response?.data?.application;

        const applicationNumber =
            application?.application_number;

        await Swal.fire({
            icon: "success",
            title: "Application Received!",
            html: `
                <div
                    style="
                        text-align:center;
                        padding:4px 8px;
                    "
                >

                    <!-- SUCCESS ICON -->
                    <div
                        style="
                            width:64px;
                            height:64px;
                            margin:0 auto 16px;
                            border-radius:50%;
                            display:flex;
                            align-items:center;
                            justify-content:center;
                            background:#EFF9F1;
                            border:1px solid #D4EED9;
                        "
                    >
                        <span
                            style="
                                font-size:30px;
                                color:#16803A;
                                font-weight:900;
                            "
                        >
                            ✓
                        </span>
                    </div>

                    <!-- THANK YOU MESSAGE -->
                    <p
                        style="
                            margin:0 0 10px;
                            color:#68736C;
                            font-size:14px;
                            line-height:1.7;
                        "
                    >
                        Thank you for your interest in
                        <strong style="color:#16803A;">
                            PASSION AIDS THE FUTURE
                        </strong>.
                    </p>

                    ${
                        applicationNumber
                            ? `
                                <!-- APPLICATION NUMBER BOX -->
                                <div
                                    style="
                                        margin:16px auto;
                                        padding:14px 14px 13px;
                                        max-width:360px;
                                        border-radius:12px;
                                        background:#FFFBE8;
                                        border:1px solid #F5D32F;
                                    "
                                >

                                    <!-- LABEL -->
                                    <div
                                        style="
                                            color:#7A6810;
                                            font-size:10px;
                                            font-weight:800;
                                            letter-spacing:1px;
                                            margin-bottom:8px;
                                        "
                                    >
                                        YOUR APPLICATION NUMBER
                                    </div>

                                    <!-- NUMBER + COPY -->
                                    <div
                                        style="
                                            display:flex;
                                            align-items:center;
                                            justify-content:center;
                                            gap:8px;
                                            flex-wrap:wrap;
                                        "
                                    >

                                        <!-- CLICKABLE APPLICATION NUMBER -->
                                        <button
                                            type="button"
                                            id="paf-application-number"
                                            title="Click to copy application number"
                                            style="
                                                border:none;
                                                background:transparent;
                                                padding:5px 7px;
                                                margin:0;
                                                color:#16803A;
                                                font-size:17px;
                                                font-weight:900;
                                                letter-spacing:.5px;
                                                cursor:pointer;
                                                border-radius:7px;
                                                transition:all .2s ease;
                                            "
                                        >
                                            ${applicationNumber}
                                        </button>

                                        <!-- COPY BUTTON -->
                                        <button
                                            type="button"
                                            id="paf-copy-application-number"
                                            style="
                                                display:inline-flex;
                                                align-items:center;
                                                justify-content:center;
                                                gap:5px;
                                                border:1px solid #16803A;
                                                background:#FFFFFF;
                                                color:#16803A;
                                                padding:7px 10px;
                                                border-radius:8px;
                                                font-size:11px;
                                                font-weight:800;
                                                cursor:pointer;
                                                transition:all .2s ease;
                                            "
                                        >
                                            <span
                                                style="
                                                    font-size:13px;
                                                "
                                            >
                                                ⧉
                                            </span>
                                            <span id="paf-copy-text">
                                                Copy
                                            </span>
                                        </button>

                                    </div>

                                    <!-- COPY INSTRUCTION -->
                                    <div
                                        style="
                                            margin-top:8px;
                                            color:#8A8260;
                                            font-size:10px;
                                            line-height:1.5;
                                        "
                                    >
                                        Click the number or Copy button
                                        to copy it.
                                    </div>

                                </div>
                            `
                            : ""
                    }

                    <!-- NEXT STEP MESSAGE -->
                    <p
                        style="
                            margin:12px 0 0;
                            color:#68736C;
                            font-size:13px;
                            line-height:1.7;
                        "
                    >
                        Your entrance application has been
                        submitted successfully. Continue to the
                        next step to enter your application number
                        and proceed with the payment procedure.
                    </p>

                    ${
                        applicationNumber
                            ? `
                                <div
                                    style="
                                        margin-top:12px;
                                        padding:9px 11px;
                                        border-radius:8px;
                                        background:#F5FAF6;
                                        border:1px solid #E0EEE3;
                                        color:#52705A;
                                        font-size:10px;
                                        line-height:1.5;
                                    "
                                >
                                    <strong>
                                        Important:
                                    </strong>
                                    Please save your application
                                    number. You will need it to
                                    access your entrance portal.
                                </div>
                            `
                            : ""
                    }

                </div>
            `,
            confirmButtonText:
                "Continue to Enter Payment Procedure",
            confirmButtonColor: COLORS.green,
            background: COLORS.white,
            color: COLORS.text,
            allowOutsideClick: false,
            allowEscapeKey: false,

            customClass: {
                popup:
                    "passion-application-alert",
                title:
                    "passion-application-alert-title",
                confirmButton:
                    "passion-application-alert-button",
            },

            showClass: {
                popup:
                    "swal2-show passion-alert-show",
            },

            hideClass: {
                popup:
                    "swal2-hide passion-alert-hide",
            },

            didOpen: () => {
                if (!applicationNumber) {
                    return;
                }

                const applicationNumberElement =
                    document.getElementById(
                        "paf-application-number"
                    );

                const copyButton =
                    document.getElementById(
                        "paf-copy-application-number"
                    );

                const copyText =
                    document.getElementById(
                        "paf-copy-text"
                    );

                const copyApplicationNumber =
                    async () => {
                        try {
                            await navigator.clipboard.writeText(
                                applicationNumber
                            );

                            if (copyText) {
                                copyText.textContent =
                                    "Copied!";
                            }

                            if (copyButton) {
                                copyButton.style.background =
                                    "#16803A";

                                copyButton.style.color =
                                    "#FFFFFF";

                                copyButton.style.borderColor =
                                    "#16803A";
                            }

                            if (
                                applicationNumberElement
                            ) {
                                applicationNumberElement.style.background =
                                    "#EFF9F1";

                                applicationNumberElement.style.color =
                                    "#075B2A";
                            }

                            setTimeout(() => {
                                if (copyText) {
                                    copyText.textContent =
                                        "Copy";
                                }

                                if (copyButton) {
                                    copyButton.style.background =
                                        "#FFFFFF";

                                    copyButton.style.color =
                                        "#16803A";

                                    copyButton.style.borderColor =
                                        "#16803A";
                                }

                                if (
                                    applicationNumberElement
                                ) {
                                    applicationNumberElement.style.background =
                                        "transparent";

                                    applicationNumberElement.style.color =
                                        "#16803A";
                                }
                            }, 1800);
                        } catch (copyError) {
                            console.error(
                                "Application number copy error:",
                                copyError
                            );

                            Swal.fire({
                                icon: "warning",
                                title: "Copy Failed",
                                text:
                                    "The application number could not be copied automatically. Please select and copy it manually.",
                                confirmButtonText:
                                    "OK",
                                confirmButtonColor:
                                    COLORS.green,
                            });
                        }
                    };

                if (applicationNumberElement) {
                    applicationNumberElement.addEventListener(
                        "click",
                        copyApplicationNumber
                    );

                    applicationNumberElement.addEventListener(
                        "mouseenter",
                        () => {
                            applicationNumberElement.style.background =
                                "#EFF9F1";
                        }
                    );

                    applicationNumberElement.addEventListener(
                        "mouseleave",
                        () => {
                            applicationNumberElement.style.background =
                                "transparent";
                        }
                    );
                }

                if (copyButton) {
                    copyButton.addEventListener(
                        "click",
                        copyApplicationNumber
                    );

                    copyButton.addEventListener(
                        "mouseenter",
                        () => {
                            copyButton.style.background =
                                "#EFF9F1";
                        }
                    );

                    copyButton.addEventListener(
                        "mouseleave",
                        () => {
                            if (
                                copyText?.textContent !==
                                "Copied!"
                            ) {
                                copyButton.style.background =
                                    "#FFFFFF";
                            }
                        }
                    );
                }
            },
        }).then(() => {
            navigate("/entranceLogin");
        });

        setFormData({
            first_name: "",
            last_name: "",
            email: "",
            phone: "",
            program: "",
            education_level: "",
            location: "",
        });
    } catch (error) {
        console.error(
            "ENTRANCE APPLICATION ERROR:",
            error
        );

        const responseData =
            error?.response?.data;

        let message =
            "We could not submit your application at this time. Please try again.";

        if (responseData?.detail) {
            message = String(
                responseData.detail
            );
        } else if (responseData?.message) {
            message = String(
                responseData.message
            );
        } else if (
            responseData &&
            typeof responseData === "object"
        ) {
            const messages = [];

            Object.entries(responseData).forEach(
                ([field, value]) => {
                    if (Array.isArray(value)) {
                        value.forEach((item) => {
                            if (
                                item !== null &&
                                item !== undefined
                            ) {
                                messages.push(
                                    String(item)
                                );
                            }
                        });
                    } else if (
                        typeof value === "string"
                    ) {
                        messages.push(value);
                    }
                }
            );

            if (messages.length > 0) {
                message =
                    messages.join("\n");
            }
        }

        showAlert({
            icon: "error",
            title: "Application Not Submitted",
            text: message,
            confirmButtonText: "Try Again",
        });
    } finally {
        setLoading(false);
    }
};

    /* =====================================================
       FIELD STYLING
    ===================================================== */

    const fieldSx = {
        "& .MuiOutlinedInput-root": {
            minHeight: 52,
            borderRadius: "12px",
            background: COLORS.input,

            transition: "all .25s ease",

            "& fieldset": {
                borderColor: COLORS.border,
                transition: "all .25s ease",
            },

            "&:hover": {
                background: COLORS.white,

                "& fieldset": {
                    borderColor: "#9BC7AA",
                },
            },

            "&.Mui-focused": {
                background: COLORS.white,

                boxShadow:
                    "0 0 0 3px rgba(22,128,58,.07)",

                "& fieldset": {
                    borderColor: COLORS.green,
                    borderWidth: 1.5,
                },
            },
        },

        "& input": {
            fontSize: ".79rem",
            color: COLORS.text,
        },

        "& .MuiSelect-select": {
            fontSize: ".79rem",
            color: COLORS.text,
        },
    };


    const iconSx = {
        fontSize: 18,
        color: COLORS.green,
    };


    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <>
            <style>
                {`
                    .passion-application-alert {
                        border-radius: 22px !important;
                        border: 1px solid #DCE6DE !important;
                        box-shadow:
                            0 25px 70px rgba(7,16,10,.18) !important;
                        padding: 28px 25px 25px !important;
                    }

                    .passion-application-alert-title {
                        color: #07100A !important;
                        font-size: 22px !important;
                        font-weight: 900 !important;
                        letter-spacing: -.3px !important;
                    }

                    .passion-application-alert-button {
                        border-radius: 10px !important;
                        padding: 11px 23px !important;
                        font-weight: 800 !important;
                        font-size: 13px !important;
                        box-shadow:
                            0 8px 18px rgba(22,128,58,.18) !important;
                        transition: all .2s ease !important;
                    }

                    .passion-application-alert-button:hover {
                        transform: translateY(-1px);
                        box-shadow:
                            0 12px 25px rgba(22,128,58,.25) !important;
                    }

                    .passion-alert-show {
                        animation:
                            passionAlertIn .35s
                            cubic-bezier(.22,1,.36,1) both;
                    }

                    .passion-alert-hide {
                        animation:
                            passionAlertOut .2s ease both;
                    }

                    @keyframes passionAlertIn {
                        from {
                            opacity: 0;
                            transform:
                                translateY(15px)
                                scale(.96);
                        }

                        to {
                            opacity: 1;
                            transform:
                                translateY(0)
                                scale(1);
                        }
                    }

                    @keyframes passionAlertOut {
                        from {
                            opacity: 1;
                            transform:
                                translateY(0)
                                scale(1);
                        }

                        to {
                            opacity: 0;
                            transform:
                                translateY(8px)
                                scale(.98);
                        }
                    }

                    .passion-input-label {
                        user-select: none;
                    }
                `}
            </style>

            <motion.div
                initial={{
                    opacity: 0,
                    x: 75,
                }}
                animate={{
                    opacity: 1,
                    x: 0,
                }}
                exit={{
                    opacity: 0,
                    x: -75,
                }}
                transition={{
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1],
                }}
                style={{
                    width: "100%",
                }}
            >

                <Box
                    sx={{
                        position: "relative",

                        p: {
                            xs: 2.2,
                            sm: 3,
                            md: 3.6,
                        },

                        borderRadius: {
                            xs: "20px",
                            sm: "24px",
                        },

                        background:
                            "rgba(255,255,255,.97)",

                        border:
                            `1px solid ${COLORS.border}`,

                        boxShadow:
                            "0 28px 75px rgba(7,16,10,.13)",

                        overflow: "hidden",

                        "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,

                            background:
                                `linear-gradient(
                                    90deg,
                                    ${COLORS.green},
                                    ${COLORS.lemon},
                                    ${COLORS.yellow}
                                )`,
                        },
                    }}
                >

                    {/* =========================================
                        HEADER
                    ========================================= */}

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 2,

                            pb: 2.4,
                            mb: 2.5,

                            borderBottom:
                                `1px solid ${COLORS.border}`,
                        }}
                    >

                        <Box>

                            <Typography
                                sx={{
                                    fontSize: ".62rem",
                                    fontWeight: 950,
                                    letterSpacing: 1.8,
                                    color: COLORS.green,
                                }}
                            >
                                PASSION AIDS THE FUTURE
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.65,

                                    fontSize: {
                                        xs: "1.65rem",
                                        sm: "1.95rem",
                                    },

                                    fontWeight: 950,
                                    letterSpacing: "-1px",
                                    lineHeight: 1.05,

                                    color: COLORS.black,
                                }}
                            >
                                Entrance Application
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.85,
                                    color: COLORS.muted,
                                    fontSize: ".77rem",
                                    lineHeight: 1.6,
                                    maxWidth: 365,
                                }}
                            >
                                Complete the short application
                                below to begin your journey
                                with us.
                            </Typography>

                        </Box>


                        <motion.div
                            animate={{
                                y: [0, -4, 0],
                                rotate: [0, 1.5, 0],
                            }}
                            transition={{
                                duration: 3.5,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >

                            <Box
                                sx={{
                                    width: 50,
                                    height: 50,
                                    flexShrink: 0,

                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",

                                    borderRadius: "14px",

                                    background:
                                        `linear-gradient(
                                            135deg,
                                            ${COLORS.black},
                                            ${COLORS.green}
                                        )`,

                                    boxShadow:
                                        "0 12px 25px rgba(7,16,10,.15)",
                                }}
                            >

                                <School
                                    sx={{
                                        color: COLORS.lemon,
                                        fontSize: 25,
                                    }}
                                />

                            </Box>

                        </motion.div>

                    </Box>


                    {/* =========================================
                        INFORMATION NOTE
                    ========================================= */}

                    <motion.div
                        initial={{
                            opacity: 0,
                            y: 8,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                        }}
                        transition={{
                            delay: 0.15,
                            duration: 0.45,
                        }}
                    >

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,

                                mb: 2.4,
                                px: 1.4,
                                py: 1.15,

                                borderRadius: "10px",

                                background:
                                    COLORS.paleGreen,

                                border:
                                    "1px solid rgba(22,128,58,.10)",
                            }}
                        >

                            <VerifiedUser
                                sx={{
                                    fontSize: 17,
                                    color: COLORS.green,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: ".65rem",
                                    lineHeight: 1.5,
                                    fontWeight: 700,
                                    color: COLORS.greenDark,
                                }}
                            >
                                Complete the application below.
                                Our admissions team will review
                                your information and contact you
                                about the next step.
                            </Typography>

                        </Box>

                    </motion.div>


                    {/* =========================================
                        FORM
                    ========================================= */}

                    <form onSubmit={handleSubmit}>

                        {/* FIRST / LAST NAME */}

                        <Box
                            sx={{
                                display: "grid",

                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "1fr 1fr",
                                },

                                gap: 1.5,
                            }}
                        >

                            <FormField
                                label="FIRST NAME"
                                required
                            >
                                <TextField
                                    fullWidth
                                    placeholder="First name"
                                    value={formData.first_name}
                                    onChange={handleChange(
                                        "first_name"
                                    )}
                                    required
                                    autoComplete="given-name"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Person
                                                    sx={iconSx}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldSx}
                                />
                            </FormField>


                            <FormField
                                label="LAST NAME"
                                required
                            >
                                <TextField
                                    fullWidth
                                    placeholder="Last name"
                                    value={formData.last_name}
                                    onChange={handleChange(
                                        "last_name"
                                    )}
                                    required
                                    autoComplete="family-name"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Person
                                                    sx={iconSx}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldSx}
                                />
                            </FormField>

                        </Box>


                        {/* EMAIL / PHONE */}

                        <Box
                            sx={{
                                display: "grid",

                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "1fr 1fr",
                                },

                                gap: 1.5,
                                mt: 1.6,
                            }}
                        >

                            <FormField
                                label="EMAIL ADDRESS"
                                required
                            >
                                <TextField
                                    fullWidth
                                    type="email"
                                    placeholder="Email address"
                                    value={formData.email}
                                    onChange={handleChange(
                                        "email"
                                    )}
                                    required
                                    autoComplete="email"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email
                                                    sx={iconSx}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldSx}
                                />
                            </FormField>


                            <FormField
                                label="PHONE NUMBER"
                                required
                            >
                                <TextField
                                    fullWidth
                                    placeholder="+231..."
                                    value={formData.phone}
                                    onChange={handleChange(
                                        "phone"
                                    )}
                                    required
                                    autoComplete="tel"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Phone
                                                    sx={iconSx}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldSx}
                                />
                            </FormField>

                        </Box>


                        {/* PROGRAM */}

                        <Box sx={{ mt: 1.6 }}>

                            <FormField
                                label="AREA OF INTEREST"
                                required
                            >

                                <TextField
                                    select
                                    fullWidth
                                    value={formData.program}
                                    onChange={handleChange(
                                        "program"
                                    )}
                                    required
                                    SelectProps={{
                                        displayEmpty: true,
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <MenuBook
                                                    sx={iconSx}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldSx}
                                >

                                    <MenuItem
                                        value=""
                                        disabled
                                    >
                                        Select your area of interest
                                    </MenuItem>

                                    {programs.map(
                                        (program) => (
                                            <MenuItem
                                                key={program}
                                                value={program}
                                            >
                                                {program}
                                            </MenuItem>
                                        )
                                    )}

                                </TextField>

                            </FormField>

                        </Box>


                        {/* EDUCATION / LOCATION */}

                        <Box
                            sx={{
                                display: "grid",

                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "1fr 1fr",
                                },

                                gap: 1.5,
                                mt: 1.6,
                            }}
                        >

                            <FormField
                                label="EDUCATION LEVEL"
                            >

                                <TextField
                                    select
                                    fullWidth
                                    value={
                                        formData.education_level
                                    }
                                    onChange={handleChange(
                                        "education_level"
                                    )}
                                    SelectProps={{
                                        displayEmpty: true,
                                    }}
                                    sx={fieldSx}
                                >

                                    <MenuItem value="">
                                        Select level
                                    </MenuItem>

                                    {educationLevels.map(
                                        (level) => (
                                            <MenuItem
                                                key={level}
                                                value={level}
                                            >
                                                {level}
                                            </MenuItem>
                                        )
                                    )}

                                </TextField>

                            </FormField>


                            <FormField
                                label="LOCATION"
                            >

                                <TextField
                                    fullWidth
                                    placeholder="City / Community"
                                    value={formData.location}
                                    onChange={handleChange(
                                        "location"
                                    )}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LocationOn
                                                    sx={iconSx}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={fieldSx}
                                />

                            </FormField>

                        </Box>


                        {/* SUBMIT */}

                        <motion.div
                            whileHover={
                                !loading
                                    ? {
                                        scale: 1.01,
                                    }
                                    : {}
                            }
                            whileTap={
                                !loading
                                    ? {
                                        scale: 0.985,
                                    }
                                    : {}
                            }
                        >

                            <Button
                                type="submit"
                                fullWidth
                                disabled={loading}
                                sx={{
                                    mt: 2.6,

                                    minHeight: 56,

                                    borderRadius: "12px",

                                    textTransform: "none",

                                    fontSize: ".84rem",

                                    fontWeight: 950,

                                    color: COLORS.black,

                                    background:
                                        `linear-gradient(
                                            135deg,
                                            ${COLORS.lemon} 0%,
                                            ${COLORS.yellow} 100%
                                        )`,

                                    boxShadow:
                                        "0 14px 28px rgba(200,242,58,.18)",

                                    "&:hover": {
                                        background:
                                            `linear-gradient(
                                                135deg,
                                                ${COLORS.lemonBright},
                                                ${COLORS.yellowBright}
                                            )`,

                                        boxShadow:
                                            "0 18px 35px rgba(200,242,58,.25)",
                                    },

                                    "&:disabled": {
                                        color: "#697169",

                                        background:
                                            "#E1E7E1",
                                    },

                                    transition:
                                        "all .25s ease",
                                }}
                            >

                                {loading ? (

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 1,
                                        }}
                                    >

                                        <CircularProgress
                                            size={19}
                                            thickness={5}
                                            sx={{
                                                color:
                                                    COLORS.green,
                                            }}
                                        />

                                        Submitting Application...

                                    </Box>

                                ) : (

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 0.9,
                                        }}
                                    >

                                        Submit Entrance Application

                                        <Send
                                            sx={{
                                                fontSize: 17,
                                            }}
                                        />

                                    </Box>

                                )}

                            </Button>

                        </motion.div>


                        {/* BACK */}

                        <Button
                            fullWidth
                            variant="text"
                            onClick={onClose}
                            startIcon={
                                <ArrowBack />
                            }
                            disabled={loading}
                            sx={{
                                mt: 0.8,

                                color: COLORS.muted,

                                fontSize: ".72rem",

                                fontWeight: 800,

                                textTransform: "none",

                                "&:hover": {
                                    background:
                                        COLORS.paleGreen,

                                    color:
                                        COLORS.black,
                                },
                            }}
                        >
                            Back to Application Start
                        </Button>

                    </form>

                </Box>


                {/* =========================================
                    FOOTER
                ========================================= */}

                <Box
                    sx={{
                        mt: 2,
                        textAlign: "center",
                    }}
                >

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 0.7,
                            mb: 0.7,
                        }}
                    >

                        <CheckCircle
                            sx={{
                                fontSize: 14,
                                color: COLORS.green,
                            }}
                        />

                        <Typography
                            sx={{
                                fontSize: ".59rem",
                                fontWeight: 900,
                                letterSpacing: 0.7,
                                color: COLORS.green,
                            }}
                        >
                            PROFESSIONAL • VOCATIONAL • LEADERSHIP
                        </Typography>

                    </Box>

                    <Typography
                        sx={{
                            fontSize: ".59rem",
                            color: COLORS.muted,
                            lineHeight: 1.5,
                        }}
                    >
                        Your information is used only for
                        admissions and institutional communication.
                    </Typography>

                </Box>

            </motion.div>
        </>
    );
};


/* =========================================================
   REUSABLE FORM LABEL
========================================================= */

const FormField = ({
    label,
    required = false,
    children,
}) => {
    return (
        <Box>

            <Typography
                className="passion-input-label"
                sx={{
                    mb: 0.65,
                    ml: 0.2,

                    fontSize: ".63rem",

                    fontWeight: 900,

                    color: COLORS.text,

                    letterSpacing: ".1px",
                }}
            >
                {label}

                {required && (
                    <Box
                        component="span"
                        sx={{
                            ml: 0.25,
                            color: COLORS.green,
                        }}
                    >
                        *
                    </Box>
                )}
            </Typography>

            {children}

        </Box>
    );
};


export default ApplyForEntranceForm;